// Which Section-detail surface currently answers the keyboard.
//
// More than one can be open at once. The KPI detail modals and the route-driven detail overlay are
// siblings in the Active groups page, each renders its own `SectionDetailHeader`, and each header
// binds Escape and the arrow keys on `window` in the capture phase. Open a weight modal and then
// arrive at the detail route without closing it and both were live, so one ArrowLeft emitted two
// `navigate`s and moved the reader two Sections along.
//
// Last-opened wins. Each header is behind a `v-if` on its own section, so it mounts exactly when its
// surface opens and unmounts when it closes - which makes registration order the same as the
// stacking order a reader perceives, without anything having to track z-index or route state.
//
// A module rather than something declared in the header itself: a `<script setup>` block's body runs
// once per component instance, so a list declared there would be per-instance and could never see
// the other surface. And a module rather than provide/inject, because the competing surfaces are
// siblings - there is no common ancestor below the page to hang a provider on.
const openSurfaces: symbol[] = [];

// Claims the keyboard for a newly-opened surface. Returns its own identity token, which it hands
// back on close.
export function pushDetailSurface(): symbol {
    const surface = Symbol('section-detail-surface');
    openSurfaces.push(surface);
    return surface;
}

// Releases a closed surface's claim. Safe to call with a token that is no longer registered.
export function popDetailSurface(surface: symbol): void {
    const at = openSurfaces.indexOf(surface);
    if (at !== -1) openSurfaces.splice(at, 1);
}

// Whether this surface is the one the reader opened last, and so the one whose keyboard shortcuts
// should fire.
export function isTopmostDetailSurface(surface: symbol): boolean {
    return openSurfaces[openSurfaces.length - 1] === surface;
}
